import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, GraduationCap, CheckCircle2, Award } from "lucide-react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";

export default function FinalExam() {
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/course/final-exam")
      .then(({ data }) => setExam(data))
      .catch((e) => setError(apiError(e)));
  }, []);

  const submit = async () => {
    if (Object.keys(answers).length < (exam?.questions?.length || 0)) {
      toast.error("Please answer every question before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/course/final-exam", { answers });
      setResult(data);
      if (data.passed) toast.success(`Certified! You scored ${data.score}%.`);
      else toast.error(`Scored ${data.score}%. You need ${data.pass_threshold}% to certify.`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <p className="text-[#C47A53] font-medium">{error}</p>
        <button onClick={() => navigate("/dashboard")} className="mt-4 px-5 py-2.5 rounded-full bg-[#2C4A3E] text-[#FAF8F5]">Back to dashboard</button>
      </div>
    );
  }
  if (!exam) return <div className="max-w-3xl mx-auto px-4 py-16 text-[#5A6B61]">Loading exam…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-[#2C4A3E] mb-6" data-testid="exam-back">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </button>

      <div className="text-center">
        <span className="w-14 h-14 mx-auto rounded-full bg-[#2C4A3E] flex items-center justify-center">
          <GraduationCap className="w-7 h-7 text-[#E8ECE8]" />
        </span>
        <h1 className="mt-5 font-serif text-3xl sm:text-4xl text-[#1A2621]">{exam.title}</h1>
        <p className="mt-2 text-[#5A6B61]">{exam.total_questions} questions · {exam.pass_threshold}% required to certify</p>
      </div>

      {result && (
        <div className={`mt-8 rounded-2xl p-6 text-center ${result.passed ? "bg-[#3D7A5A]/10 border border-[#3D7A5A]/30" : "bg-[#C47A53]/10 border border-[#C47A53]/30"}`}>
          <p className="font-serif text-4xl text-[#1A2621]">{result.score}%</p>
          <p className={`mt-1 font-medium ${result.passed ? "text-[#3D7A5A]" : "text-[#C47A53]"}`}>
            {result.passed ? "Congratulations — you are certified!" : `You need ${result.pass_threshold}% to certify.`}
          </p>
          <p className="text-sm text-[#5A6B61] mt-1">{result.correct} of {result.total} correct</p>
          <div className="mt-4 flex flex-wrap gap-3 justify-center">
            {result.passed ? (
              <button onClick={() => navigate("/certificate")} data-testid="view-certificate-btn" className="px-6 py-3 rounded-full bg-[#C69255] text-[#1A2621] font-semibold flex items-center gap-2">
                <Award className="w-4 h-4" /> View your certificate
              </button>
            ) : (
              <button onClick={() => { setResult(null); setAnswers({}); }} data-testid="retry-exam-btn" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-[#FAF8F5] font-semibold">Retake exam</button>
            )}
          </div>
        </div>
      )}

      {!result && (
        <>
          <div className="mt-8 space-y-5">
            {exam.questions.map((q, qi) => (
              <div key={q.id} data-testid={`exam-q-${qi}`} className="bg-[#F3EFEA] border border-[#2C4A3E]/12 rounded-2xl p-5">
                <p className="font-medium text-[#1A2621]">{qi + 1}. {q.question}</p>
                <div className="mt-3 space-y-2">
                  {q.options.map((opt, oi) => {
                    const selected = answers[q.id] === oi;
                    return (
                      <button
                        key={oi}
                        data-testid={`exam-q-${qi}-opt-${oi}`}
                        onClick={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                        className={`w-full text-left p-3.5 rounded-xl border flex items-center gap-3 transition-all ${selected ? "bg-[#829B88]/15 border-[#2C4A3E]/50" : "bg-white/60 border-[#2C4A3E]/15 hover:bg-[#829B88]/10"}`}
                      >
                        <span className={`w-6 h-6 shrink-0 rounded-full border flex items-center justify-center text-xs font-mono ${selected ? "bg-[#2C4A3E] text-[#FAF8F5] border-[#2C4A3E]" : "border-[#2C4A3E]/30 text-[#5A6B61]"}`}>
                          {String.fromCharCode(65 + oi)}
                        </span>
                        <span className="text-sm text-[#334139]">{opt}</span>
                        {selected && <CheckCircle2 className="w-4 h-4 text-[#2C4A3E] ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-sm text-[#5A6B61] text-center">
            {Object.keys(answers).length}/{exam.questions.length} answered
          </div>
          <button onClick={submit} disabled={submitting} data-testid="exam-submit-btn" className="mt-3 w-full py-4 rounded-xl bg-[#2C4A3E] text-[#FAF8F5] font-semibold hover:bg-[#223B31] transition-colors disabled:opacity-60">
            {submitting ? "Grading…" : "Submit final exam"}
          </button>
        </>
      )}
    </div>
  );
}
